/**
 * Hand-rolled history-API router for the Ember SPA (route table: 新设计文档 §2).
 *
 * No dependency: parse window.location.pathname into a discriminated Route,
 * navigate via pushState/replaceState + a custom event, re-render on popstate.
 * <Link> (components/Link.tsx) and useRoute() are the only two touchpoints
 * pages need.
 */

import { createContext, useContext, useEffect, useState } from 'react';

export type EmberRoute =
  | { name: 'landing' }
  | { name: 'talk' }
  | { name: 'auth' }
  // tab is a legacy suffix (old two-tab result links); the page is single now.
  | { name: 'result'; conversationId: string; tab: 'ember' | 'questions' | null }
  | { name: 'settings' } // /settings — account settings index
  | { name: 'settings-space' } // /settings/space — retired 2026-09-01; EmberApp replaces to /me/embers (the header edits the symbol + line)
  | { name: 'feedback' } // /feedback — message board to the team, signed in or not
  | { name: 'me' } // '/me' — EmberApp replaces to /me/embers
  | { name: 'me-embers' }
  | { name: 'me-letters' } // /me/letters — Ember's weekly letters (§4.20 third tab, 2026-09-23)
  | { name: 'chats' } // /chats — every conversation the person has: Ember first, then their rooms (2026-10-20)
  | { name: 'room'; roomId: string } // /rooms/:roomId — a room's page, members only (§4.21)
  // New friends (2026-10-26 founder): the page, setting up a request, an introduction, the week's check-in, a topic to
  // put up, and a topic room someone else put up (to ask into, or take Ember's invitation into).
  | { name: 'friends' } // /friends
  | { name: 'friends-new' } // /friends/new
  | { name: 'friend-intro'; roomId: string } // /friends/intro/:roomId
  | { name: 'friend-checkin'; roomId: string } // /friends/checkin/:roomId
  | { name: 'topic-new' } // /friends/rooms/new
  | { name: 'topic-room'; roomId: string } // /friends/rooms/:roomId
  // Groups (2026-11-03 founder): the groups open to them, a group, putting up a post, a post and its answers.
  | { name: 'groups' } // /groups
  | { name: 'group'; groupId: string } // /groups/:groupId
  | { name: 'group-new'; groupId: string } // /groups/:groupId/new
  | { name: 'group-post'; postId: string } // /groups/p/:postId
  | { name: 'me-letter-detail'; letterId: string; read?: boolean } // /me/letters/:id — one letter's page, opening on their path (2026-11-03); /me/letters/:id/read — the letter itself; opening either is reading it
  // /me/unlock — where a texted notebook-reset link lands (§4.12, 2026-09-07)
  | { name: 'me-unlock' }
  | { name: 'me-ember-detail'; emberId: string }
  // /me/embers/:id/transcript — the telling the Ember was written from (§4.13)
  | { name: 'me-ember-transcript'; emberId: string }
  | { name: 'person'; token: string } // /p/:token — Human Presence person page (S15, §4.16)
  | { name: 'handoff'; token: string } // /e/:token — an Ember its owner handed to someone (S17, §4.18)
  | { name: 'letter-handoff'; token: string } // /l/:token — a weekly letter its reader handed to someone (§4.20, 2026-09-24)
  | { name: 'read'; readId: string } // /read/:readId — an Ember someone let this person read (§4.9, 2026-09-27)
  | { name: 'how-it-went'; precedentId: string } // /how-it-went/:id — how it went for others who stood where this person stands (§4.22, 2026-10-05)
  | { name: 'question'; questionId: string } // /q/:questionId — the question page (§4.19, 2026-09-20)
  | { name: 'place'; slug: string } // /places/:slug — one light of the Banyan (S16, §4.17)
  | { name: 'legal-update' } // /legal/update — what changed in the Terms and Privacy Policy (2026-10-15)
  | { name: 'age' } // /age — the age check's question (2026-10-27)
  // A page the product no longer has: the discovery surfaces retired on
  // 2026-09-04 (/requests…, /themes…, /me/requests), the Mattered tab folded
  // into Embers on 2026-09-25 (/me/mattered) and the v3.0 public Ember page
  // retired on 2026-09-30 (/embers/:ref, /topic/:slug); the notebook's Questions
  // and Rooms tabs (2026-10-20: Ask people stopped 2026-09-26, the rooms moved to
  // the Chats list). EmberApp replaces onto `to`.
  | { name: 'retired'; to: string }
  | { name: 'not-found' };

export interface RouteLocation {
  route: EmberRoute;
  /** Raw pathname (no query/hash). */
  path: string;
  search: URLSearchParams;
  /** Includes the leading '#', or ''. */
  hash: string;
}

const NAV_EVENT = 'ember:navigation';
const routeScroll = new Map<string, number>();
let scrollFrame = 0;

/**
 * How the location last changed — the telemetry dimension page.navigated
 * carries as `method`. push = a forward step the person chose; replace = a
 * redirect/alias rewrite (not a choice); pop = the history moved backward or
 * forward (browser chrome, or the in-page Back popping entries). Set here at
 * the moment of the move because the emitter (EmberApp's route effect) only
 * sees the resulting location, never the cause.
 */
export type NavigationMethod = 'push' | 'replace' | 'pop';
let lastNavMethod: NavigationMethod = 'push';
export function lastNavigationMethod(): NavigationMethod {
  return lastNavMethod;
}

function locationKey(url: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

export function parseRoute(pathname: string): EmberRoute {
  // A trail entry carries its query (2026-10-19): the route is the path alone.
  const parts = pathname.split(/[?#]/)[0].split('/').filter(Boolean).map((p) => {
    try { return decodeURIComponent(p); } catch { return p; }
  });
  if (parts.length === 0) return { name: 'landing' };
  const [head, second, third] = parts;
  switch (head) {
    // Client half of the retired-URL rule. The server answers these paths
    // with a 301 before the SPA ever loads (RETIRED_PRODUCT_PREFIXES in
    // src/edge/seo/legacy-marketing-redirects.js — keep the two in step);
    // this covers an in-app <Link> that never reaches the server.
    case 'requests':
    case 'themes':
    case 'embers':
    case 'topic':
      return { name: 'retired', to: '/' };
    case 'talk':
      return parts.length === 1 ? { name: 'talk' } : { name: 'not-found' };
    case 'chats':
      return parts.length === 1 ? { name: 'chats' } : { name: 'not-found' };
    case 'auth':
      return parts.length === 1 ? { name: 'auth' } : { name: 'not-found' };
    case 'result':
      if (parts.length === 2) return { name: 'result', conversationId: second, tab: null };
      if (parts.length === 3 && (third === 'ember' || third === 'questions')) {
        return { name: 'result', conversationId: second, tab: third };
      }
      return { name: 'not-found' };
    case 'me':
      if (parts.length === 1) return { name: 'me' };
      if ((second === 'requests' || second === 'mattered') && parts.length >= 2) return { name: 'retired', to: '/me/embers' };
      if (second === 'embers' && parts.length === 2) return { name: 'me-embers' };
      if (second === 'letters' && parts.length === 2) return { name: 'me-letters' };
      if (second === 'letters' && parts.length === 3) return { name: 'me-letter-detail', letterId: third };
      if (second === 'letters' && parts.length === 4 && parts[3] === 'read') return { name: 'me-letter-detail', letterId: third, read: true };
      if (second === 'questions' && parts.length === 2) return { name: 'retired', to: '/me/embers' };
      if (second === 'rooms' && parts.length === 2) return { name: 'retired', to: '/chats' };
      if (second === 'unlock' && parts.length === 2) return { name: 'me-unlock' };
      if (second === 'embers' && parts.length === 3) return { name: 'me-ember-detail', emberId: third };
      if (second === 'embers' && parts.length === 4 && parts[3] === 'transcript') {
        return { name: 'me-ember-transcript', emberId: third };
      }
      return { name: 'not-found' };
    case 'settings':
      if (parts.length === 1) return { name: 'settings' };
      if (second === 'space' && parts.length === 2) return { name: 'settings-space' };
      return { name: 'not-found' };
    case 'feedback':
      return parts.length === 1 ? { name: 'feedback' } : { name: 'not-found' };
    case 'p':
      return parts.length === 2 ? { name: 'person', token: second } : { name: 'not-found' };
    case 'e':
      return parts.length === 2 ? { name: 'handoff', token: second } : { name: 'not-found' };
    case 'l':
      return parts.length === 2 ? { name: 'letter-handoff', token: second } : { name: 'not-found' };
    case 'read':
      return parts.length === 2 ? { name: 'read', readId: second } : { name: 'not-found' };
    case 'how-it-went':
      return parts.length === 2 ? { name: 'how-it-went', precedentId: second } : { name: 'not-found' };
    case 'q':
      return parts.length === 2 ? { name: 'question', questionId: second } : { name: 'not-found' };
    case 'rooms':
      return parts.length === 2 ? { name: 'room', roomId: second } : { name: 'not-found' };
    case 'friends':
      if (parts.length === 1) return { name: 'friends' };
      if (parts.length === 2 && second === 'new') return { name: 'friends-new' };
      if (parts.length === 3 && second === 'intro') return { name: 'friend-intro', roomId: third };
      if (parts.length === 3 && second === 'checkin') return { name: 'friend-checkin', roomId: third };
      if (parts.length === 3 && second === 'rooms') return third === 'new' ? { name: 'topic-new' } : { name: 'topic-room', roomId: third };
      return { name: 'not-found' };
    case 'groups':
      if (parts.length === 1) return { name: 'groups' };
      if (parts.length === 3 && second === 'p') return { name: 'group-post', postId: third };
      if (parts.length === 2) return { name: 'group', groupId: second };
      if (parts.length === 3 && third === 'new') return { name: 'group-new', groupId: second };
      return { name: 'not-found' };
    case 'places':
      return parts.length === 2 ? { name: 'place', slug: second } : { name: 'not-found' };
    case 'legal':
      return parts.length === 2 && second === 'update' ? { name: 'legal-update' } : { name: 'not-found' };
    case 'age':
      return parts.length === 1 ? { name: 'age' } : { name: 'not-found' };
    default:
      return { name: 'not-found' };
  }
}

function snapshot(): RouteLocation {
  const { pathname, search, hash } = window.location;
  return {
    route: parseRoute(pathname),
    path: pathname,
    search: new URLSearchParams(search),
    hash,
  };
}

/**
 * Where a route sits in the site hierarchy (§3 of the UI standard): the home
 * page is level 1, its sections are level 2, request/ember details are level
 * 3, and an Ember inside a request is level 4. A theme landing is level 2
 * with the sections: it is a curated list of requests, so Back from a request
 * opened there must return to it. `null` marks the flow pages (talk, auth,
 * result) that live outside the ladder — they keep their own return
 * contracts. Settings hangs off Profile, so it counts as level 3 even though
 * its URL is top-level: Back from it must not skip over /me.
 */
export function routeLevel(pathname: string): number | null {
  switch (parseRoute(pathname).name) {
    case 'landing':
      return 1;
    case 'me':
    case 'me-embers':
    case 'me-letters':
    case 'feedback':
      return 2;
    case 'me-ember-detail':
    case 'me-letter-detail':
    case 'settings':
    case 'person':
      return 3;
    case 'question':
    case 'read':
    case 'how-it-went':
      return 2;
    case 'settings-space':
    case 'me-ember-transcript':
      return 4;
    default:
      return null;
  }
}

/**
 * The structural parent a hierarchical page falls back to when the visitor's
 * own trail holds nothing shallower — a cold arrival from a text message or a
 * share link. Derivable from the URL alone; `null` for flow pages.
 */
export function canonicalParent(pathname: string): string | null {
  const route = parseRoute(pathname);
  switch (route.name) {
    case 'me':
    case 'me-embers':
    case 'me-letters':
    case 'feedback':
      return '/';
    case 'room':
      return '/chats';
    case 'friends':
      return '/';
    case 'friends-new':
    case 'friend-intro':
    case 'friend-checkin':
    case 'topic-new':
    case 'topic-room':
      return '/friends';
    case 'groups':
      return '/friends';
    case 'group':
    case 'group-post':
      return '/groups';
    case 'group-new':
      return `/groups/${encodeURIComponent(route.groupId)}`;
    case 'me-letter-detail':
      return '/me/letters';
    case 'me-ember-detail':
    case 'settings':
      return '/me/embers';
    case 'me-ember-transcript':
      return `/me/embers/${encodeURIComponent(route.emberId)}`;
    case 'settings-space':
      return '/settings';
    // A person page is reached from a readable Ember; a cold arrival falls
    // back home (it used to name /requests, which has redirected home since
    // 2026-09-04).
    case 'person':
      return '/';
    default:
      return null;
  }
}

/**
 * Per-entry breadcrumbs stamped into the history state — the only per-entry
 * storage the platform gives us: `history.length` counts the whole tab,
 * including entries from before Ember was ever opened. `emberDepth` is how
 * many SPA pushes deep this entry is (0 = the entry the document loaded on);
 * `emberTrail` is the path of every entry from that load up to and
 * including this one, so Back can pop several hops at once with
 * `history.go(-k)` instead of walking them. A path keeps its query
 * (2026-10-19): /talk is the chat only with ?mode=chat, and Back says
 * "Back to chat" when that is where it lands.
 */
interface EmberHistoryState { emberDepth?: number; emberTrail?: string[] }

function historyDepth(): number {
  const state = window.history.state as EmberHistoryState | null;
  const depth = state && typeof state.emberDepth === 'number' ? state.emberDepth : 0;
  return depth > 0 ? depth : 0;
}

/** The visitor's trail, ending at the current entry. Entries created before
 *  this stamping existed collapse to just the current path. */
function historyTrail(): string[] {
  if (typeof window === 'undefined') return ['/'];
  const state = window.history.state as EmberHistoryState | null;
  const trail = state?.emberTrail;
  if (Array.isArray(trail) && trail.length > 0 && trail.every((p) => typeof p === 'string')) {
    return trail;
  }
  return [`${window.location.pathname}${window.location.search}`];
}

function trailEntry(to: string): string {
  try {
    const url = new URL(to, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch {
    return to.split('#')[0] || '/';
  }
}

/** Whether a path is the person's conversation — /talk?mode=chat, a room's thread on it included. */
export function isChatPath(path: string): boolean {
  try {
    const url = new URL(path, 'https://ember.space');
    return url.pathname === '/talk' && url.searchParams.get('mode') === 'chat';
  } catch {
    return false;
  }
}

function restoreScroll(): void {
  const top = routeScroll.get(locationKey(window.location)) ?? 0;
  window.cancelAnimationFrame(scrollFrame);
  // Let React commit the destination first; restoring against the previous
  // page's height can clamp a valid saved offset to zero.
  scrollFrame = window.requestAnimationFrame(() => {
    window.scrollTo({ top, left: 0, behavior: 'auto' });
  });
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  // A route owns its scroll position, in both directions.
  routeScroll.set(locationKey(window.location), window.scrollY);
  const trail = historyTrail();
  const path = trailEntry(to);
  const state: EmberHistoryState = opts.replace
    ? { emberDepth: historyDepth(), emberTrail: [...trail.slice(0, -1), path] }
    : { emberDepth: historyDepth() + 1, emberTrail: [...trail, path] };
  if (opts.replace) window.history.replaceState(state, '', to);
  else window.history.pushState(state, '', to);
  lastNavMethod = opts.replace ? 'replace' : 'push';
  window.dispatchEvent(new Event(NAV_EVENT));
  restoreScroll();
}

/**
 * Rewrite the current URL in place without announcing a navigation — query
 * sync (a filter, a tab, a returnTo the page just learned). Goes through the
 * router so the entry keeps its depth stamp; a bare replaceState would erase
 * it and make Back believe there is nothing behind this page.
 */
export function replaceUrl(to: string): void {
  const state: EmberHistoryState = {
    emberDepth: historyDepth(),
    emberTrail: [...historyTrail().slice(0, -1), trailEntry(to)],
  };
  window.history.replaceState(state, '', to);
}

/** Same-origin path the document was entered from, or '' — the entry context
 *  a page-level Back falls back to when the route carries no returnTo. */
export function entryReferrerPath(): string {
  if (typeof document === 'undefined' || typeof window === 'undefined') return '';
  try {
    if (!document.referrer) return '';
    const ref = new URL(document.referrer);
    if (ref.origin !== window.location.origin) return '';
    if (ref.pathname === window.location.pathname) return '';
    return `${ref.pathname}${ref.search}`;
  } catch {
    return '';
  }
}

/** What the page-level Back will actually do, resolved ahead of the click so
 *  the control can render an honest href and accessible label. */
export type BackResolution =
  | { kind: 'pop'; steps: number; path: string } // history.go(-steps), landing on path
  | { kind: 'leave'; path: string } // history.back() out of this document, to the same-origin referrer
  | { kind: 'navigate'; path: string }; // replace the current entry with path

/**
 * The Back rule (§3 of the UI standard): Back goes *up one level*. Walk the
 * visitor's own trail backwards and pop to the nearest entry that sits
 * shallower in the hierarchy than the current page — however many lateral
 * hops between details that skips — so every landing is still a page they
 * actually visited, with its scroll intact. A flow page along the way (a
 * result, a sign-in) counts as a stop: it is the context the visitor came
 * through, not a rung to skip. Flow pages themselves keep the old
 * return-one-step contract. Only when the trail holds nothing usable does
 * Back navigate — to the route's returnTo, else the page's structural parent
 * — and that trip replaces the entry rather than pushing one: a Back that
 * stacks a new entry is how the same page ends up in history twice.
 *
 * Pure core, exported for tests; `resolveBack` binds it to the live history.
 */
export function resolveBackFrom(
  trail: string[],
  returnTo: string | null | undefined,
  referrer: string,
  safeDestination = '/',
): BackResolution {
  const current = trail[trail.length - 1] ?? '/';
  const level = routeLevel(current);
  if (level === null) {
    if (trail.length > 1) return { kind: 'pop', steps: 1, path: trail[trail.length - 2] };
  } else {
    // The structural parent already in the trail is a rung even when it sits
    // on the same level (the theme archive behind a theme landing): popping
    // to it beats replacing this entry with a duplicate of it.
    const parent = canonicalParent(current);
    for (let i = trail.length - 2; i >= 0; i--) {
      const behind = routeLevel(trail[i]);
      if (behind === null || behind < level || trail[i].split('?')[0] === parent) {
        return { kind: 'pop', steps: trail.length - 1 - i, path: trail[i] };
      }
    }
  }
  // The document-load entry with a same-origin page behind it (/collect, a
  // server-rendered surface): popping is still a true return.
  if (trail.length === 1 && referrer) return { kind: 'leave', path: referrer };
  const destination = sanitizeReturnTo(returnTo, '')
    || (level === null ? '' : canonicalParent(current) ?? '')
    || safeDestination;
  return { kind: 'navigate', path: destination };
}

export function resolveBack(returnTo?: string | null, safeDestination = '/'): BackResolution {
  return resolveBackFrom(historyTrail(), returnTo, entryReferrerPath(), safeDestination);
}

/** Whether Back leaves this page for the person's conversation (2026-10-19): Back then says "Back to chat".
 *  Not from the conversation page itself — between it and a room's thread the header's own pill goes. */
export function backLeavesForChat(target: BackResolution): boolean {
  if (typeof window === 'undefined') return false;
  return isChatPath(target.path) && !isChatPath(`${window.location.pathname}${window.location.search}`);
}

/**
 * Back from a conversation for someone with a Chats list (2026-10-20): to the
 * list, wherever they came in from — pop to the nearest /chats behind this
 * entry, or, with none (a room's link from a text, the chat from home), put
 * the list in this entry's place. Pure core, exported for tests.
 */
export function chatsBackFrom(trail: string[]): BackResolution {
  return listBackFrom(trail, '/chats');
}

/** Pop to the nearest `list` behind this entry, or put it in this entry's place when none is there. */
function listBackFrom(trail: string[], list: string): BackResolution {
  for (let i = trail.length - 2; i >= 0; i--) {
    if (trail[i].split('?')[0] === list) return { kind: 'pop', steps: trail.length - 1 - i, path: trail[i] };
  }
  return { kind: 'navigate', path: list };
}

/**
 * Back to New friends once a step there is done — a request sent, a topic put up, an introduction answered Not now
 * (release check 2026-10-27): a replace onto /friends left the /friends the person came from behind it, so the trail
 * read [..., /friends, /friends] and the page's Back landed on the same page. Pure core, exported for tests.
 */
export function friendsBackFrom(trail: string[]): BackResolution {
  return listBackFrom(trail, '/friends');
}

/** Done on a New friends step: to the /friends behind it, or in its place (friendsBackFrom). */
export function goFriends(): void {
  const step = friendsBackFrom(historyTrail());
  if (step.kind === 'pop') {
    window.history.go(-step.steps);
    return;
  }
  navigate('/friends', { replace: true });
}

/**
 * Back to a group once a post of theirs is up (Groups, 2026-11-03 founder: land on the group, the new post at its top):
 * the group page behind the compose page, popped to, or put in its place — never a second copy of it on top of the
 * first. Pure core, exported for tests.
 */
export function groupBackFrom(trail: string[], groupId: string): BackResolution {
  return listBackFrom(trail, `/groups/${encodeURIComponent(groupId)}`);
}

/** Done posting: to the group (groupBackFrom), at its top, where the new post is. */
export function goGroup(groupId: string): void {
  const step = groupBackFrom(historyTrail(), groupId);
  routeScroll.set(step.path, 0);
  if (step.kind === 'pop') {
    window.history.go(-step.steps);
    return;
  }
  navigate(step.path, { replace: true });
}

export function resolveChatsBack(): BackResolution {
  return chatsBackFrom(historyTrail());
}

/** The conversation's Back to the list (components/ChatsBack.tsx). */
export function goChats(): void {
  const step = resolveChatsBack();
  if (step.kind === 'pop') {
    window.history.go(-step.steps);
    return;
  }
  navigate('/chats', { replace: true });
  lastNavMethod = 'pop';
}

/** The page-level Back action (components/PageBack.tsx is its caller of note). */
export function goBack(returnTo?: string | null, safeDestination = '/'): void {
  const step = resolveBack(returnTo, safeDestination);
  if (step.kind === 'pop') {
    window.history.go(-step.steps);
    return;
  }
  if (step.kind === 'leave') {
    window.history.back();
    return;
  }
  navigate(sanitizeReturnTo(step.path, safeDestination), { replace: true });
  // The replace above is still the person pressing Back — the entry mechanics
  // are a replace, the choice is a pop. Set AFTER navigate (which stamped
  // 'replace'): React commits the new location asynchronously, so the route
  // effect reads this corrected value.
  lastNavMethod = 'pop';
}

/** How many entries to pop to land on home, or null if home is not behind
 *  this entry. Pure core for goHome, exported for tests. */
export function homePopSteps(trail: string[]): number | null {
  for (let i = trail.length - 2; i >= 0; i--) {
    if (routeLevel(trail[i]) === 1) return trail.length - 1 - i;
  }
  return null;
}

/**
 * The detail-page Home action (components/PageHome.tsx): collapse back to the
 * home entry already in the trail so the stack shrinks, or push home when the
 * visitor has never been there this trip — a real forward step the browser's
 * Back can then undo.
 */
export function goHome(): void {
  const steps = homePopSteps(historyTrail());
  if (steps) {
    window.history.go(-steps);
    return;
  }
  navigate('/');
}

// A popstate fires after the URL has already changed, too late to read the
// outgoing page's offset — so record it as the reader scrolls instead.
if (typeof window !== 'undefined') {
  let recordFrame = 0;
  window.addEventListener('scroll', () => {
    window.cancelAnimationFrame(recordFrame);
    recordFrame = window.requestAnimationFrame(() => {
      routeScroll.set(locationKey(window.location), window.scrollY);
    });
  }, { passive: true });
  // Fragment jumps (`<a href="#…">`, the homepage "How it works" links) are
  // same-document navigations that ALSO fire popstate — after the browser has
  // already positioned the target. Restoring a stored offset there yanks the
  // reader straight back off the section they asked for, so a popstate that
  // kept the document and carries a hash leaves the browser's scroll alone.
  const documentKey = (): string => `${window.location.pathname}${window.location.search}`;
  let lastDocumentKey = documentKey();
  window.addEventListener(NAV_EVENT, () => { lastDocumentKey = documentKey(); });
  window.addEventListener('popstate', () => {
    // Registered at module eval, so this runs before useRoute's own popstate
    // handler re-renders — the method is already 'pop' when the route effect
    // reads it.
    lastNavMethod = 'pop';
    const sameDocument = documentKey() === lastDocumentKey;
    lastDocumentKey = documentKey();
    if (sameDocument && window.location.hash) return;
    restoreScroll();
  });
}

/**
 * Navigation guards (2026-09-03). A page that must not be left by the
 * browser's own Back — the toolbar arrow, an iOS edge swipe — registers one.
 * A popstate cannot be cancelled: by the time it fires the history has already
 * moved. So a guard sees the location the move landed on and answers `true`
 * to let the route follow, or `false` to HOLD — the route stays where it was,
 * nothing re-renders, and the guard itself undoes the move
 * (`history.go(±1)`) and asks its question. The undo fires a second popstate
 * back onto the guarded page, which the guard lets through. Programmatic
 * navigate() calls are never guarded: a page that wants to intercept its own
 * controls does so at the control (PageBack / PageHome `onIntercept`).
 */
export type NavigationGuard = (next: RouteLocation) => boolean;
const navigationGuards = new Set<NavigationGuard>();

export function registerNavigationGuard(guard: NavigationGuard): () => void {
  navigationGuards.add(guard);
  return () => { navigationGuards.delete(guard); };
}

/** Current location; updates on pushState (via navigate) and popstate. */
/**
 * The route the server is rendering, for every useRoute() below it. Public
 * pages are rendered once on the server and then hydrated in the browser. The
 * server has no window.location, so its renderer supplies the route it
 * rendered — once, at the top of the tree, so a hook deep in the nav (the
 * notification badge, say) needs no plumbing to be renderable there. The
 * browser provides null and keeps using the live location snapshot.
 */
const SsrLocationContext = createContext<RouteLocation | null>(null);
export const SsrLocationProvider = SsrLocationContext.Provider;

export function useRoute(): RouteLocation {
  const ssrLocation = useContext(SsrLocationContext);
  const [loc, setLoc] = useState<RouteLocation>(() => ssrLocation ?? snapshot());
  useEffect(() => {
    const update = (): void => setLoc(snapshot());
    // Guards see a popstate before the route follows it (registerNavigationGuard).
    const onPop = (): void => {
      const next = snapshot();
      for (const guard of navigationGuards) {
        if (guard(next) === false) return;
      }
      setLoc(next);
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener(NAV_EVENT, update);
    // A navigation made before this listener was on is caught up here: a child's effect runs before its parent's, so a
    // page that moves on as it mounts (/chats on a wide window → the conversation) changed the URL with no one listening,
    // and the old page stayed under the new address (2026-10-26 walk-through). The query is read by its text, not
    // URLSearchParams.size (undefined before iOS Safari 17 — release check 2026-10-27).
    setLoc((cur) => (locationKey({ pathname: cur.path, search: cur.search.toString() ? `?${cur.search}` : '', hash: cur.hash }) === locationKey(window.location) ? cur : snapshot()));
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener(NAV_EVENT, update);
    };
  }, []);
  return loc;
}

/**
 * returnTo hygiene (§2.1): only same-origin path strings from the allowlist —
 * the result flow ('/result/…') plus internal pages. Anything else (absolute
 * URLs, protocol-relative '//', backslash tricks) falls back. No open redirect.
 */
export function sanitizeReturnTo(raw: string | null | undefined, fallback = '/'): string {
  if (!raw) return fallback;
  const value = raw.trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return fallback;
  const pathOnly = value.split(/[?#]/)[0];
  const ok = pathOnly === '/'
    || pathOnly === '/me' || pathOnly.startsWith('/me/')
    || pathOnly === '/requests' || pathOnly.startsWith('/requests/')
    || pathOnly === '/themes' || pathOnly.startsWith('/themes/')
    || pathOnly === '/talk'
    || pathOnly === '/chats'
    || pathOnly.startsWith('/rooms/')
    // New friends (2026-10-27 walk-through: a texted introduction or check-in link opened signed out landed on home)
    || pathOnly === '/friends' || pathOnly.startsWith('/friends/')
    // Groups (2026-11-03): a texted answer or ask link opened signed out comes back to it
    || pathOnly === '/groups' || pathOnly.startsWith('/groups/')
    || pathOnly === '/settings' || pathOnly.startsWith('/settings/')
    || pathOnly === '/collect'
    || pathOnly.startsWith('/p/')
    || pathOnly.startsWith('/result/');
  return ok ? value : fallback;
}

/** Attach a safe in-app return destination without dropping the target hash. */
export function withReturnTo(to: string, returnTo: string | null | undefined): string {
  const safe = sanitizeReturnTo(returnTo, '');
  if (!safe) return to;
  const hashAt = to.indexOf('#');
  const path = hashAt >= 0 ? to.slice(0, hashAt) : to;
  const hash = hashAt >= 0 ? to.slice(hashAt) : '';
  return `${path}${path.includes('?') ? '&' : '?'}returnTo=${encodeURIComponent(safe)}${hash}`;
}

/** Human label for a sanitized return destination. */
export function returnToLabel(returnTo: string, fallback: string): string {
  const path = sanitizeReturnTo(returnTo, '').split(/[?#]/)[0];
  if (!path) return fallback;
  if (path === '/') return 'Home';
  if (path === '/me' || path === '/me/embers' || path === '/me/letters') return 'Notebook';
  if (path === '/chats') return 'Chats';
  if (path.startsWith('/me/embers/')) return 'Ember';
  if (path.startsWith('/result/')) return 'Your Ember';
  if (path === '/talk') return 'Ways to talk';
  if (path === '/settings') return 'Settings';
  if (path.startsWith('/p/')) return 'Person page';
  // A Meet people page's Back named Home while it went to /friends (release check 2026-10-27); a room's, the same.
  if (path === '/friends' || path.startsWith('/friends/')) return 'Meet people';
  if (path === '/groups') return 'Groups';
  if (path.startsWith('/groups/p/')) return 'the post';
  if (path.startsWith('/groups/')) return 'the group';
  if (path.startsWith('/rooms/')) return 'Room';
  return fallback;
}
